<template>
  <div class="app">
    <t-navbar
      :nav-items="this.$getPrimaryNavItems('msp')"
      :user="authUser"
      v-model="closed"
    >
      <template #header>
        <t-nav-select
          v-if="userAccountList"
          label="Customers"
          data-test-id="navbar-customer-select"
          @selected="customerSelected"
          :closed="closed"
          :loading="loading"
          :options="userAccountList"
          :initial-selected="initialCustomer"
        />
        <b-skeleton
          v-else
          class="mt-6"
          animation="wave"
          height="56px"
          width="100%"
        />
      </template>
    </t-navbar>
    <div
      class="app-content"
      v-if="this.selectedUser"
      :key="this.selectedUser.id"
    >
      <main>
        <b-container fluid="lg">
          <t-header
            :user="authUser"
            :user-links="userLinks"
            :product-links="productLinks"
            :version="version"
            :license="license"
          />
          <router-view />
        </b-container>
      </main>
      <t-footer
        class="footer-image-bg"
        :version="appVersion"
        :logo="this.uiLogo"
        :nameservers="nameservers"
        :productLinks="footerProducts"
      />
      <t-modal />
      <t-toast />
      <t-alert />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { version } from "../../package";
import {
  PRODUCTS_LINK,
  PRODUCTS_ICON,
  PRODUCTS_TITLE,
  PRODUCTS_DESCRIPTION,
} from "@/constants";

import TNavbar from "@/components/TNavbar";
import TModal from "@/components/TModal";

export default {
  components: {
    TNavbar,
    TModal,
  },
  data() {
    return {
      version: null,
      license: null,
      appVersion: version,
      initialCustomer: null,
      userAccountList: null,
      closed: false,
      loading: true,
      nameservers: [],
      productLinks: [
        {
          title: PRODUCTS_TITLE.WEB,
          link: "#",
          active: true,
          icon: PRODUCTS_ICON.WEB,
          description: this.$t(PRODUCTS_DESCRIPTION.WEB),
          versionName: "- Errigal",
        },
        {
          title: PRODUCTS_TITLE.ARC,
          link: PRODUCTS_LINK.ARC,
          active: false,
          icon: PRODUCTS_ICON.ARC,
          description: this.$t(PRODUCTS_DESCRIPTION.ARC),
        },
        {
          title: PRODUCTS_TITLE.SAFE,
          link: PRODUCTS_LINK.SAFE,
          active: false,
          icon: PRODUCTS_ICON.SAFE,
          description: this.$t(PRODUCTS_DESCRIPTION.SAFE),
        },
        {
          title: PRODUCTS_TITLE.SPAM,
          link: PRODUCTS_LINK.SPAM,
          active: false,
          icon: PRODUCTS_ICON.SPAM,
          description: this.$t(PRODUCTS_DESCRIPTION.SPAM),
        },
      ],
      userLinks: [],
      footerLogo:
        this.$userPrefs.getCurrentUi().logo ||
        require("@/assets/branding/titanhq-logo.svg"),
    };
  },
  computed: {
    ...mapGetters("authentication", [
      "selectedUser",
      "authUser",
      "mspId",
      "role",
    ]),
    ...mapGetters("users", ["userAccounts"]),
    ...mapGetters("support", ["support"]),
    ...mapGetters("userInterface", ["interface", "uiLogo"]),
    footerProducts() {
      return this.productLinks.slice(1, 4);
    },
    isLoggedIn() {
      return this.userAccounts !== null && this.role <= 2;
    },
    name() {
      return this.authUser !== null ? this.authUser.accountname : "";
    },
    email() {
      return this.authUser !== null ? this.authUser.email : "";
    },
  },
  watch: {
    userAccountList() {
      this.setSelectedUser();
    },

    userAccounts() {
      this.userAccountList = this.cleanCustomerAccounts(this.userAccounts);
    },
    support() {
      this.setSupportLinks(this.support);
    },
  },
  methods: {
    ...mapActions("users", ["fetchCustomerAccounts", "fetchCustomerAccount"]),
    ...mapActions("support", ["fetchSupport"]),
    ...mapActions("authentication", ["setUser", "setMspId"]),
    ...mapActions("userInterface", ["fetchInterfaceBatch", "fetchInterface"]),
    toggleNavigation() {
      this.closed = !this.closed;
    },
    onWindowResize(matchMedia) {
      if (matchMedia.matches && this.closed !== true) {
        this.closed = true;
      }

      if (!matchMedia.matches && this.closed !== false) {
        this.closed = false;
      }
    },
    async customerSelected(customer) {
      this.setUser(customer);
      localStorage.setItem("selectedCustomer", customer.id);
      if (customer.allMsps) {
        await this.fetchInterface({ mspId: null });
        this.$router.push("/system/overview");
      } else if (customer.allCustomers) {
        // this.$router.push("/msp/overview");
      } else if (this.$route.path !== "/customer/overview") {
        this.$router.push("/customer/overview");
      }
    },
    setSelectedUser() {
      this.initialCustomer = 0;
    },
    cleanCustomerAccounts(accounts) {
      const allAccounts = accounts
        .map((acct) => {
          return {
            ...acct,
            name: acct.accountname,
            testID: `navbar-customer-select-${acct.accountname
              .toLowerCase()
              .replace(/\s/g, "-")}`,
            allCustomers: false,
            short: this.$getAccountShortName(acct.accountname),
          };
        })
        .sort((a, b) =>
          a.name.toLowerCase() > b.name.toLowerCase()
            ? 1
            : b.name.toLowerCase() > a.name.toLowerCase()
            ? -1
            : 0
        );

      let otherAccounts = [
        { name: "All Customers", short: "AC", id: 0, allCustomers: true },
      ];
      if (this.role === 1) {
        otherAccounts.push({
          name: "All MSPs",
          short: "AM",
          allMsps: true,
          allCustomers: false,
        });
      }
      return [...otherAccounts, ...allAccounts];
    },
    setSupportLinks(support) {
      this.userLinks = [
        {
          title: this.$t("Security"),
          link: "/profile/security",
          icon: "gear",
        },
      ];

      if (support.link) {
        this.userLinks.push({
          title: this.$t("Support"),
          link: this.support.link,
          icon: "life-preserver",
        });
      }

      if (support.email) {
        this.userLinks.push({
          title: this.$t("Contact"),
          link: `mailto:${this.support.email}`,
          icon: "chat-left",
        });
      }
    },
  },
  beforeCreate() {
    const userToken = this.$userPrefs.getCurrentToken();
    if (!userToken) this.$router.push("/");
    this.$http.setAuthorisationToken(userToken);
  },
  beforeMount() {
    this.setUser({
      name: "All Customers",
      short: "AC",
      id: 0,
      allCustomers: true,
      uuid_str: this.userAccounts[0]?.uuid_str,
    });
  },
  async created() {
    if (!this.userAccounts.length)
      await this.fetchCustomerAccounts({ msp_id: this.mspId });
    this.userAccountList = this.cleanCustomerAccounts(this.userAccounts);
    await this.fetchSupport(false);
    this.setSupportLinks(this.support);

    this.loading = false;

    await this.fetchInterfaceBatch();
    //this.nameservers = this.interface.nameservers;
    this.version = String(this.interface.system.version);
    this.license = String(this.interface.system.license_number);
  },
};
</script>

<style lang="scss" scoped>
.app {
  display: flex;
  height: 100vh;
  overflow: hidden;

  &-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    footer {
      margin-top: auto;
    }
  }

  .app-content {
    background-color: $bg-light;
  }
}

@media only screen and (max-width: 1200px) {
  .app {
    &-content {
      margin-left: 3.5rem;
    }
  }
}
</style>
